// Customers sometimes type tickets in full caps to get attention.
// These de-shout the words that are actually shouted and leave the rest of the
// text as the customer typed it, so they keep working on partially fixed text.

const MINOR_WORDS = new Set([
  "a", "an", "and", "as", "at", "but", "by", "for", "in", "nor", "of", "on",
  "or", "the", "to", "v", "via", "vs",
]);

// Kept in caps when the rest of the text is de-shouted
const ACRONYMS = new Set([
  "ABS", "BMW", "DPF", "DVLA", "ECU", "EGR", "EU", "GB", "HGV", "MOT", "PDF",
  "PO", "RRP", "SAT", "UK", "USB", "VAT", "VIN", "VW", "4WD", "4X4",
]);

// Splitting these on punctuation would mangle them
const isAddress = (word: string) =>
  /@|:\/\/|^www\.|\.(com|co\.uk|org|net|io|uk)\b/i.test(word);

// Adds the missing space in "hello,world", "Heck.YOU" and "I.will". Needs a
// letter before the punctuation (so decimals survive) and two or more letters
// after it, which keeps "e.g." and initials like "J.R.R." together.
export const addSpaceAfterPunctuation = (text: string) =>
  text.replace(/\S+/g, (word) =>
    isAddress(word) ? word : word.replace(/([a-zA-Z][.,!?;:]+)(?=[a-zA-Z]{2})/g, "$1 ")
  );

const letters = (word: string) => word.replace(/[^a-zA-Z]/g, "");

// Digits usually mean a registration, part number or model - leave those alone.
const isProtected = (word: string) =>
  /\d/.test(word) || ACRONYMS.has(word.replace(/[^a-zA-Z0-9]/g, "").toUpperCase());

const isShouted = (word: string) =>
  !isProtected(word) &&
  letters(word).length >= 1 &&
  letters(word) !== "I" &&
  word === word.toUpperCase();

// A single caps word is emphasis, more than that is shouting. One-letter words
// are too weak a signal to count towards that, but still get fixed once it trips.
const shouldFix = (text: string) =>
  (text.match(/\S+/g) ?? []).filter((w) => isShouted(w) && letters(w).length >= 2).length >= 2;

const ENDS_SENTENCE = /[.!?]+["')\]]*$/;

const deEmphasise = (word: string) =>
  isProtected(word) || letters(word) === "I" ? word : word.toLowerCase();

// "I. Will. Get. My. Point. Across" is the same trick as full caps, so runs of
// three or more one-word sentences get merged back into a single sentence.
const collapseLine = (line: string) => {
  const words = line.match(/\S+/g) ?? [];
  const output: string[] = [];
  let collapsed = false;
  let i = 0;

  while (i < words.length) {
    let end = i;
    while (end < words.length && ENDS_SENTENCE.test(words[end])) end += 1;

    if (end - i >= 3) {
      // Swallow the rest of the sentence too, so "YOU.WILL.ACTION.THIS" ends up
      // as one sentence rather than leaving "This" stranded after a full stop.
      while (end < words.length && !ENDS_SENTENCE.test(words[end])) end += 1;
      if (end < words.length) end += 1;

      const run = words.slice(i, end);
      // Only the run's final full stop survives the merge.
      output.push(
        run
          .map((w, index) => deEmphasise(index === run.length - 1 ? w : w.replace(ENDS_SENTENCE, "")))
          .join(" ")
      );
      collapsed = true;
      i = end;
    } else {
      output.push(words[i]);
      i += 1;
    }
  }

  return collapsed ? output.join(" ") : line;
};

// Lists on separate lines are usually genuine, so runs never span a line break.
const collapseStaccato = (text: string) =>
  text.split("\n").map(collapseLine).join("\n");

const toTitleCase = (text: string) => {
  let index = 0;
  return text.replace(/\S+/g, (word) => {
    const isFirst = index === 0;
    index += 1;

    // Words the customer cased themselves are left as they typed them.
    const needsRecase = !isProtected(word) && (isShouted(word) || word === word.toLowerCase());
    if (!needsRecase) return word;

    const lower = word.toLowerCase();
    if (!isFirst && MINOR_WORDS.has(letters(lower))) return lower;
    return lower.replace(/[a-z]/, (c) => c.toUpperCase());
  });
};

const toSentenceCase = (text: string) =>
  text
    .replace(/\S+/g, (word) => (isShouted(word) ? word.toLowerCase() : word))
    // Capitalise the first letter of the text and of each new sentence or line.
    .replace(/(^|[.!?]\s+|\n\s*)([a-z])/g, (_, prefix, letter) => prefix + letter.toUpperCase())
    // Standalone "i" only - "will.i" is not a pronoun.
    .replace(/(^|\s)i(?=\s|[.,!?;:]|$)/g, "$1I");

// Casing is only rewritten once the text shows it is being used for emphasis.
const prepare = (text: string) => {
  const spaced = addSpaceAfterPunctuation(text).trim();
  const collapsed = collapseStaccato(spaced);
  return { text: collapsed, needsFix: collapsed !== spaced || shouldFix(spaced) };
};

export const formatTicketSubject = (raw: string) => {
  const { text, needsFix } = prepare(raw);
  return needsFix ? toTitleCase(text) : text;
};

export const formatTicketDescription = (raw: string) => {
  const { text, needsFix } = prepare(raw);
  return needsFix ? toSentenceCase(text) : text;
};
